import * as React from "react";
import { SyntheticEvent } from "react";
import { Control, FieldPathValue, FieldValues, Path, useController } from "react-hook-form";
import { DropdownOnSearchChangeData, DropdownProps, Form, FormDropdownProps } from "semantic-ui-react";

interface SemanticReactHookFormsDropdownProps<
    TFormModel extends FieldValues,
    TName extends Path<TFormModel>
> extends Omit<FormDropdownProps, "name" | "value" | "onChange" | "onBlur" | "control" | "options" | "fluid" | "selection" | "error"> {
    name: FieldPathValue<TFormModel, TName> extends FormDropdownProps["value"] ? TName : never;
    control: Control<TFormModel>;
    options: FormDropdownProps["options"];
}

export const SemanticReactHookFormsDropdown = <
    TFormModel extends FieldValues,
    TName extends Path<TFormModel>
>({ control, name, ...props }: SemanticReactHookFormsDropdownProps<TFormModel, TName>) => {
    const {
        field: { onChange: onFieldChange, value, ref, ...field },
        fieldState: { error }
    } = useController({
        name,
        control
    });
    const onChange = React.useCallback((_, data: DropdownProps) => {
        onFieldChange(data.value);
    }, [onFieldChange]);
    const [searchQuery, setSearchQuery] = React.useState<string>("");

    const handleSearchChange = React.useCallback((_: SyntheticEvent<HTMLElement, Event>, data: DropdownOnSearchChangeData): void => {
        setSearchQuery(data.searchQuery);
    }, []);

    return (
        <>
            <Form.Dropdown
                {...props}
                {...field}
                fluid
                selection
                onChange={onChange}
                onSearchChange={handleSearchChange}
                searchQuery={searchQuery}
                value={value}
                error={error?.message}
            />
        </>
    );
};
